/* AgriEID redesign: large player for the video cards (ae-video-grid, ae-how-video, ae-video-split, ae-info-tabs).
   A click on Play on a video card opens the video in a player about three times the size of the card, centred
   over the page, and starts it. It grows out of the card and shrinks back into it. Close with the button, Esc or
   a click beside the video. Works for an MP4 card (data-mp4) and for a Vimeo card (data-vimeo, or data-ae-vimeo
   written as id or id/code for an unlisted video).
   A card that already fills most of the screen (phones) plays in place: ae-redesign.js does that for MP4 and
   data-vimeo cards, and here a data-ae-vimeo card is swapped for Vimeo's own player as soon as the page loads,
   which is how those videos were shown before they had cards.
   A Vimeo card marked data-ae-vcover shows the theme picture assets/ae-vimeo-<id>.jpg; when the theme has no
   such file (a video added later) the cover is fetched from Vimeo.
   Vimeo videos written into a product description (sections/ae-description.liquid) become the same cards on a
   computer or tablet; on a phone they stay the description's own players.
   No dependencies. Own file, styles included, so it can change without touching ae-redesign.js or .css. */
(function () {
  'use strict';

  var AE = window.AE = window.AE || {};
  if (AE.videoPop) return;                 // several sections load this file on one page

  var view = null;                         // the open player
  var styled = false;
  var EASE = 'cubic-bezier(.2,.7,.2,1)';
  var CARD = '[data-ae-pop] .ae-video[data-mp4],[data-ae-pop] .ae-video[data-vimeo],[data-ae-pop] .ae-video[data-ae-vimeo]';
  var ALLOW = 'autoplay; fullscreen; picture-in-picture';

  var CSS = [
    '.ae-vpop{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(9,16,31,0);transition:background .3s ease}',
    '.ae-vpop.is-open{background:rgba(9,16,31,.88)}',
    '.ae-vpop__box{position:relative;width:min(100%,1040px,calc((100vh - 170px)*16/9));min-width:min(100%,280px)}',
    '.ae-vpop__frame{aspect-ratio:16/9;background:#000 center/cover no-repeat;border-radius:16px;overflow:hidden;box-shadow:0 30px 80px rgba(0,0,0,.55);transform-origin:50% 50%;transition:transform .34s ' + EASE + '}',
    '.ae-vpop__frame video{display:block;width:100%;height:100%;background:#000}',
    '.ae-vpop__frame iframe{display:block;width:100%;height:100%;border:0}',
    '.ae-vpop__title{display:block;margin:16px 0 0;color:#fff;font:600 18px/1.3 "Plus Jakarta Sans",system-ui,sans-serif;letter-spacing:-0.01em;opacity:0;transition:opacity .2s ease}',
    '.ae-vpop__close{position:absolute;top:-56px;right:0;width:44px;height:44px;margin:0;padding:0;border:0;border-radius:50%;background:#fff;color:#000080;cursor:pointer;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .2s ease}',
    '.ae-vpop__close:focus-visible{outline:3px solid #F5C518;outline-offset:3px}',
    '.ae-vpop.is-open .ae-vpop__title,.ae-vpop.is-open .ae-vpop__close{opacity:1;transition-delay:.18s}',
    '@media (prefers-reduced-motion:reduce){.ae-vpop,.ae-vpop__frame,.ae-vpop__title,.ae-vpop__close{transition:none!important}}'
  ].join('');

  function style() {
    if (styled) return;
    styled = true;
    var el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  function still() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  }

  // the card's Vimeo video: {id, h} (h = the link code of an unlisted video), or null for an MP4 card
  function vimeoOf(card) {
    var v = card.getAttribute('data-ae-vimeo') || card.getAttribute('data-vimeo');
    if (!v) return null;
    var p = String(v).replace('?h=', '/').split('/');
    var id = (p[0] || '').replace(/\D/g, '');
    return id ? { id: id, h: (p[1] || '').replace(/[^0-9a-z]/gi, '') } : null;
  }

  function vimeoSrc(v, play) {
    return 'https://player.vimeo.com/video/' + v.id + '?' + (v.h ? 'h=' + v.h + '&' : '') +
      (play ? 'autoplay=1' : 'autoplay=0&muted=1&loop=0&playsinline=1&controls=1&autopause=0') + '&title=0&byline=0&portrait=0';
  }

  function vimeoFrame(v, play, title) {
    var f = document.createElement('iframe');
    f.src = vimeoSrc(v, play);
    f.allow = ALLOW;
    f.setAttribute('allowfullscreen', '');
    f.title = title;
    return f;
  }

  function titleOf(card) {
    var t = card.getAttribute('data-title');
    if (!t) {
      var wrap = card.parentNode && card.parentNode.parentNode;
      var h3 = wrap && wrap.querySelector('h3');
      t = h3 ? h3.textContent : '';
    }
    return (t || '').replace(/\s+/g, ' ').trim() || 'Video';
  }

  function coverOf(card) {
    var im = card.querySelector('img');
    if (im && im.naturalWidth) return im.currentSrc || im.src;
    var m = /url\(["']?([^"')]+)["']?\)/.exec(window.getComputedStyle(card).backgroundImage || '');
    return m ? m[1] : '';
  }

  // Vimeo's own player in the card's place, as these videos were shown before they had cards
  function inline(card, play) {
    var v = vimeoOf(card);
    if (!v || !card.parentNode) return;
    var f = vimeoFrame(v, play, titleOf(card));
    if (!play) f.loading = 'lazy';
    f.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0';
    card.parentNode.replaceChild(f, card);
  }

  function wide(card) {
    var w = card.getBoundingClientRect().width;
    return w > 0 && w >= window.innerWidth * 0.6;
  }

  // the transform that puts the large frame exactly over the card it came from
  function over(card, frame) {
    var a = card.getBoundingClientRect(), b = frame.getBoundingClientRect();
    if (!a.width || !b.width) return '';
    var dx = (a.left + a.width / 2) - (b.left + b.width / 2);
    var dy = (a.top + a.height / 2) - (b.top + b.height / 2);
    return 'translate(' + dx + 'px,' + dy + 'px) scale(' + (a.width / b.width) + ')';
  }

  function close() {
    if (!view) return;
    var v = view; view = null;
    document.removeEventListener('keydown', v.onKey, true);
    document.removeEventListener('focusin', v.onFocus, true);
    if (v.media.tagName === 'VIDEO') { try { v.media.pause(); } catch (e) {} }
    else if (v.media.parentNode) v.media.parentNode.removeChild(v.media);   // an embedded player only stops when it leaves the page
    var done = function () {
      if (v.back.parentNode) v.back.parentNode.removeChild(v.back);
      if (v.card && v.card.focus) { try { v.card.focus({ preventScroll: true }); } catch (e) {} }
    };
    v.back.classList.remove('is-open');
    if (still()) { done(); return; }
    var r = v.card.getBoundingClientRect();
    if (r.bottom > 0 && r.top < window.innerHeight) v.frame.style.transform = over(v.card, v.frame);   // back into the card when it is on screen
    setTimeout(done, 340);
  }

  function open(card) {
    if (view) close();
    style();
    var title = titleOf(card);
    var vim = vimeoOf(card);
    var cover = coverOf(card);

    var back = document.createElement('div');
    back.className = 'ae-vpop';
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-label', title);
    back.innerHTML = '<div class="ae-vpop__box"><div class="ae-vpop__frame"></div><p class="ae-vpop__title"></p>' +
      '<button type="button" class="ae-vpop__close" aria-label="Close video"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>';
    var frame = back.querySelector('.ae-vpop__frame');
    var btn = back.querySelector('.ae-vpop__close');
    back.querySelector('.ae-vpop__title').textContent = title;

    var media;
    if (vim) {
      media = vimeoFrame(vim, true, title);
      if (cover) frame.style.backgroundImage = 'url("' + cover.replace(/"/g, '%22') + '")';   // the cover shows while Vimeo loads
    } else {
      media = document.createElement('video');
      media.src = card.getAttribute('data-mp4');
      media.controls = true; media.autoplay = true; media.playsInline = true;
      media.setAttribute('playsinline', '');
      if (cover) media.poster = cover;
    }
    frame.appendChild(media);
    document.body.appendChild(back);

    view = { back: back, frame: frame, media: media, card: card };
    view.onKey = function (e) {
      // the key stops here: on the live store the chat assistant also listens for Esc and would open as the video closes
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); e.stopPropagation(); close(); }
    };
    view.onFocus = function (e) {            // keep the keyboard inside the player while it is open
      if (view && !back.contains(e.target)) btn.focus();
    };
    document.addEventListener('keydown', view.onKey, true);
    document.addEventListener('focusin', view.onFocus, true);
    back.addEventListener('click', function (e) { if (e.target === back) close(); });
    btn.addEventListener('click', close);
    // the page behind does not scroll under the player (no overflow lock, so nothing on the page shifts)
    back.addEventListener('wheel', function (e) { e.preventDefault(); }, { passive: false });
    back.addEventListener('touchmove', function (e) { if (e.target === back) e.preventDefault(); }, { passive: false });

    if (!still()) {
      frame.style.transition = 'none';
      frame.style.transform = over(card, frame);
      void frame.offsetWidth;                // start from the card, then let go
      frame.style.transition = '';
    }
    frame.style.transform = '';
    back.classList.add('is-open');
    try { btn.focus({ preventScroll: true }); } catch (e) {}
    if (!vim) {
      var p = media.play();
      if (p && p.catch) p.catch(function () {});
    }
  }

  // Vimeo videos written into a product description. The section holds each player's address back in data-ae-vsrc
  // so that nothing loads until this runs: a cover card from 750px wide, the description's own player below that.
  var PILL = 'position:relative;background:#fff;color:#000080;font-weight:600;font-size:15px;line-height:1.2;padding:14px 22px;border-radius:999px;box-shadow:0 6px 22px rgba(9,16,31,.3)';
  function describe(root) {
    var frames = (root || document).querySelectorAll('iframe[data-ae-vsrc]');
    Array.prototype.forEach.call(frames, function (f) {
      var src = f.getAttribute('data-ae-vsrc');
      f.removeAttribute('data-ae-vsrc');
      var sec = f.closest('[data-ae-pop]');
      var m = /player\.vimeo\.com\/video\/(\d+)/.exec(src);
      if (!sec || !m || !f.parentNode || window.innerWidth < 750) { f.src = src; return; }
      var h = /[?&]h=([0-9a-z]+)/i.exec(src);
      var ref = m[1] + (h ? '/' + h[1] : '');
      var a = document.createElement('a');
      a.className = 'ae-video';
      a.href = 'https://vimeo.com/' + ref;
      a.target = '_blank'; a.rel = 'noopener';
      a.setAttribute('data-ae-vimeo', ref);
      a.setAttribute('data-ae-vcover', '');
      a.setAttribute('data-ae-big', '');                                    // always the large player, however wide the card
      a.setAttribute('data-title', sec.getAttribute('data-ae-title') || '');
      a.style.cssText = 'position:relative;display:flex;align-items:center;justify-content:center;width:100%;aspect-ratio:16/9;border-radius:16px;overflow:hidden;background:#0E1E3A;line-height:1.2;text-decoration:none';
      var im = document.createElement('img');
      im.alt = ''; im.loading = 'lazy'; im.decoding = 'async';
      im.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;max-width:none;margin:0;object-fit:cover';
      var tpl = sec.getAttribute('data-ae-cover-url') || '';                // the address of assets/ae-vimeo-0.jpg
      im.src = tpl ? tpl.replace('ae-vimeo-0.jpg', 'ae-vimeo-' + m[1] + '.jpg') + (tpl.indexOf('?') > -1 ? '&' : '?') + 'width=1200' : '';
      var pill = document.createElement('span');
      pill.className = 'ae-play';
      pill.style.cssText = PILL;
      pill.textContent = '\u25B6 ' + (sec.getAttribute('data-ae-play') || 'Play video');
      a.appendChild(im); a.appendChild(pill);
      f.parentNode.replaceChild(a, f);
    });
  }

  // Capture phase on the document: this runs before the card's own click handler in ae-redesign.js.
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var card = e.target && e.target.closest ? e.target.closest(CARD) : null;
    if (!card || card.querySelector('video,iframe')) return;                // not a card, or already playing in place
    if (wide(card) && !card.hasAttribute('data-ae-big')) {                  // already most of the screen: play in place
      if (!card.hasAttribute('data-ae-vimeo')) return;                      // ae-redesign.js plays MP4 and data-vimeo cards in the card
      e.preventDefault();
      e.stopImmediatePropagation();
      inline(card, true);
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    open(card);
  }, true);

  // Phones: a Vimeo card that fills the screen becomes Vimeo's own player straight away (one tap plays it there).
  function settle(root) {
    var cards = (root || document).querySelectorAll('[data-ae-pop] .ae-video[data-ae-vimeo]:not([data-ae-big])');
    for (var i = 0; i < cards.length; i++) if (wide(cards[i])) inline(cards[i], false);
  }

  // A cover the theme does not hold (a video added after the covers were made) comes from Vimeo instead.
  function covers(root) {
    var cards = (root || document).querySelectorAll('.ae-video[data-ae-vcover]');
    Array.prototype.forEach.call(cards, function (card) {
      var im = card.querySelector('img');
      var v = vimeoOf(card);
      if (!im || !v || im.getAttribute('data-ae-cover')) return;
      im.setAttribute('data-ae-cover', '1');
      var fetched = false;
      var swap = function () {
        if (fetched) { im.style.visibility = 'hidden'; return; }            // Vimeo's picture failed too: plain navy card
        fetched = true;
        if (!window.fetch) { im.style.visibility = 'hidden'; return; }
        var page = 'https://vimeo.com/' + v.id + (v.h ? '/' + v.h : '');
        window.fetch('https://vimeo.com/api/oembed.json?width=1280&url=' + encodeURIComponent(page))
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (!j || !j.thumbnail_url) { im.style.visibility = 'hidden'; return; }
            im.removeAttribute('srcset'); im.removeAttribute('sizes');
            im.src = j.thumbnail_url;
          })
          .catch(function () { im.style.visibility = 'hidden'; });
      };
      im.addEventListener('error', swap);
      if (im.complete && !im.naturalWidth) swap();                           // it had already failed before this script ran
    });
  }

  function start(root) { describe(root); covers(root); settle(root); }
  start(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { start(document); });
  document.addEventListener('shopify:section:load', function (e) { start(e.target); });   // theme editor

  AE.videoPop = { open: open, close: close };
})();
